/**
 * §5.3 / P-001, P-002 — the join screen.
 *
 * The first screen a player sees. The code is case-insensitive and ignores
 * whitespace, and a code that arrived from a QR link is prefilled.
 */

import { useEffect, useState } from "react";
import { BrandLockup } from "@tq/ui";
import { ApiRequestError } from "../api.js";

export interface JoinScreenProps {
  initialCode: string;
  onJoin: (code: string) => Promise<void>;
}

export function JoinScreen({ initialCode, onJoin }: JoinScreenProps): React.ReactElement {
  const [code, setCode] = useState(initialCode.toUpperCase());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // P-001 — a QR link joins without a further tap where it can.
  useEffect(() => {
    if (!initialCode) return;
    setBusy(true);
    onJoin(initialCode)
      .catch((cause) => setError(messageFor(cause)))
      .finally(() => setBusy(false));
  }, [initialCode, onJoin]);

  const submit = async () => {
    const trimmed = code.replace(/\s+/g, "").toUpperCase();
    if (trimmed.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await onJoin(trimmed);
    } catch (cause) {
      setError(messageFor(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tq-player">
      <div className="tq-player-main">
        {/* The card, not the sheet. On a phone this is the whole screen and the
            wrapper does nothing; past 640px it stops the layout stretching to
            the width of a monitor. */}
        <div className="tq-phone-join tq-phone-card">
          <div className="tq-phone-join-title">
            {/* The same lockup the projector, the console and the rest of the
                player app carry (§35.6). This screen used to show a 🏛️ emoji
                over the words "LIVE TRUSTEE TRIVIA" — a stand-in that outlived
                its purpose and then went stale, since the game is called
                Showdown everywhere else. The room code is the only thing a
                player has to act on here, and it was competing with an emoji
                for the top of the screen.

                No session is known yet — the code below is what identifies one
                — so this is the same default PlayerApp falls back to. */}
            <BrandLockup brand={{ top: "FPPTA", main: "TRUSTEE", accent: "SHOWDOWN" }} />
          </div>

          <label className="tq-phone-label" htmlFor="tq-join-code">
            ROOM CODE
          </label>
          <input
            id="tq-join-code"
            className="tq-phone-code-input"
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={24}
            // §14.2 — generated codes are four letters. A facilitator may still
            // set a longer custom code, so the field is not capped at four.
            placeholder="ABCD"
            value={code}
            disabled={busy}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            onKeyDown={(event) => {
              if (event.key === "Enter") void submit();
            }}
          />

          <div className="tq-phone-hint" role="alert">
            {error}
          </div>

          {/* The push to the bottom of the screen used to be an inline
              `marginTop: auto`, which meant it applied at every width — on a
              monitor it threw the button a thousand pixels away from the field
              it submits. It is a media query now, so the phone keeps its
              thumb-reachable bar and the desktop keeps the button with the
              form. */}
          <button
            type="button"
            className="tq-phone-submit"
            disabled={busy || code.trim().length === 0}
            onClick={() => void submit()}
          >
            {busy ? "JOINING…" : "JOIN ROOM"}
          </button>
        </div>
      </div>
    </div>
  );
}

function messageFor(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return "Could not reach the game. Check your connection and try again.";
}
